/* eslint-disable @typescript-eslint/no-explicit-any */
import _, { camelCase, upperFirst } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useWorkspaceConfiguration } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Breakpoint,
	Callout,
	Card,
	Container,
	DateIntervalNavigation,
	DateIntervalValue,
	FlexGap,
	GraphQLErrorMessage,
	Heading3,
	ITimeMachine,
	Jumbo,
	Label,
	Paragraph,
	Subheading,
	Title,
	createCustomTimeMachine,
	createTimeMachine,
	tokens,
} from '@treams/treams-ui';

import Breadcrumbs from '../goals/group-selector/Breadcrumbs';
import { SelectableGroupFilter } from '../goals/group-selector/new/GroupSelector.types';
import { GroupSelectorNew } from '../goals/group-selector/new/GroupSelectorNew';
import { GroupSelectionData } from '../goals/group-selector/new/useGroupSelection';
import AnalyticsUserFilterSelector from './AnalyticsUserFilterSelector';
import EngagementAnalytics from './EngagementAnalytics';
import AnalyticsMetricDetails from './metric/AnalyticsMetricDetails';
import { AnalyticsMetricContainer, valueFromMetric } from './metrics/AnalyticsMetric';
import { displayMetric } from './metrics/AnalyticsNumberMetric';
import { ThresholdTitle, TrafficLight } from './metrics/TrafficLight';

const Grid = styled(Box)`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: ${tokens.gap.group}px;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		grid-template-columns: minmax(0, 1fr);
	}
`;

function hasThreshold(metric: graphql.AnalyticsMetric) {
	return (
		metric.__typename === 'AnalyticsIntegerMetric' ||
		metric.__typename === 'AnalyticsFloatMetric' ||
		metric.__typename === 'AnalyticsPercentageMetric' ||
		metric.__typename === 'AnalyticsFractionMetric'
	);
}

function hasDetails(
	metric: graphql.AnalyticsMetric,
): metric is
	| graphql.AnalyticsIntegerMetric
	| graphql.AnalyticsFloatMetric
	| graphql.AnalyticsPercentageMetric
	| graphql.AnalyticsFractionMetric {
	return (
		metric.__typename === 'AnalyticsIntegerMetric' ||
		metric.__typename === 'AnalyticsFloatMetric' ||
		metric.__typename === 'AnalyticsPercentageMetric' ||
		metric.__typename === 'AnalyticsFractionMetric'
	);
}

function MetricValue(props: {
	variables: any;
	groups?: graphql.Group[];
	timeMachine: ITimeMachine;
	previous?: boolean;
	userFilter?: graphql.AnalyticsUserFilter;
	engagementOfficerMetricsOnly: boolean;
}) {
	const { t } = useTranslation();

	const { data } = graphql.useAnalyticsMetricQuery({
		variables: {
			...props.variables,
			filters: {
				groups: props.groups?.map((group) => ({
					id: group.id,
					type: group.type,
				})),
				startDate: props.timeMachine.current[0],
				endDate: props.timeMachine.current[1],
				userFilter: props.userFilter,
				engagementOfficerMetricsOnly: props.engagementOfficerMetricsOnly,
			},
		},
		fetchPolicy: 'no-cache',
	});

	const metric = data ? data.analytics.metricGroup.metric : undefined;
	const metricValue = metric && valueFromMetric(data?.analytics.metricGroup.metric);

	return (
		<>
			<Flex alignItems="center">
				<Heading3>
					<DateIntervalValue value={props.timeMachine} />
				</Heading3>
				{data && metric && hasThreshold(metric as graphql.AnalyticsMetric) ? (
					<TrafficLight
						metric={data.analytics.metricGroup.metric as graphql.AnalyticsMetric}
						value={metricValue}
						totalUsers={
							metric.id === 'competencies.total'
								? metricValue
								: metric.__typename === 'AnalyticsIntegerMetric'
									? data.analytics.totalUsers
									: metric.__typename === 'AnalyticsFractionMetric'
										? metric.total
										: undefined
						}
						threshold={
							(
								data.analytics.metricGroup.metric as
									| graphql.AnalyticsIntegerMetric
									| graphql.AnalyticsFloatMetric
									| graphql.AnalyticsPercentageMetric
									| graphql.AnalyticsFractionMetric
							).threshold
						}
					/>
				) : (
					<TrafficLight.Skeleton ml={2} />
				)}
			</Flex>
			<Paragraph mb={3}>
				{t(`analytics.metric.period.${props.previous ? 'previous' : 'current'}`)}
			</Paragraph>

			{data?.analytics.metricGroup.metric ? (
				<Title>
					{displayMetric(
						data.analytics.metricGroup.metric as
							| graphql.AnalyticsIntegerMetric
							| graphql.AnalyticsFloatMetric
							| graphql.AnalyticsPercentageMetric
							| graphql.AnalyticsFractionMetric
							| graphql.AnalyticsConversationTemplateMetric,
						valueFromMetric,
					)}
				</Title>
			) : (
				<Title.Skeleton />
			)}
		</>
	);
}

MetricValue.Skeleton = function MetricValueSkeleton() {
	return (
		<>
			<Flex alignItems="center">
				<Heading3>
					<br />
				</Heading3>
				<TrafficLight.Skeleton ml={2} />
			</Flex>
			<Paragraph.Skeleton mb={3} />
			<Title.Skeleton />
		</>
	);
};

function Threshold(props: { metric?: graphql.AnalyticsMetric; signal: 'good' | 'bad' }) {
	return (
		<AnalyticsMetricContainer py={2}>
			<Flex alignItems="center">
				{!props.metric ? (
					<Paragraph.Skeleton lines={1} />
				) : (
					<>
						<Flex alignItems="center">
							<TrafficLight.Element signal={props.signal} mr={2} />
						</Flex>

						{props.metric && (
							<Paragraph>
								<ThresholdTitle metric={props.metric} signal={props.signal} />
							</Paragraph>
						)}
					</>
				)}
			</Flex>
		</AnalyticsMetricContainer>
	);
}

export default function Metric(props: {
	groupSelection: GroupSelectionData;
	selectableGroupFilter: SelectableGroupFilter;
	timeMachine: ITimeMachine;
	onChangeTimeMachine: (newTs: ITimeMachine) => void;
	engagementOfficerMetricsOnly: boolean;
}) {
	const { t } = useTranslation();
	const configuration = useWorkspaceConfiguration();
	const groupSelection = props.groupSelection;
	const timeMachine = props.timeMachine;
	const isEngagement = location.pathname.includes('metric/engagement');

	const { params } = useRouteMatch<{ metricGroupID: string; metricID: string; tabID?: string }>();

	const sharedVariables = {
		type: graphql.AnalyticsType.Workspace,
		metricGroupID: params.metricGroupID,
		metricID: `${params.metricGroupID}.${camelCase(params.metricID)}`,
	};

	const [userFilter, setUserFilter] = useState<graphql.AnalyticsUserFilter | undefined>(
		graphql.AnalyticsUserFilter.ActiveAtEndDate,
	);
	const [hasSelectedEngagementRequest, setHasSelectedEngagementRequest] = useState(false);

	const filters: graphql.AnalyticsFiltersV2 = {
		startDate: timeMachine.current[0],
		endDate: timeMachine.current[1],
		groups: groupSelection.groupsInSelection.map((group) => _.pick(group, ['id', 'type'])),
		userFilter: userFilter,
		engagementOfficerMetricsOnly: props.engagementOfficerMetricsOnly,
	};

	const [fetchAnalyticsMetric, { data, error }] = graphql.useAnalyticsMetricLazyQuery({
		variables: {
			...sharedVariables,
			filters,
		},
	});

	const previousTimeMachine = timeMachine.previous
		? timeMachine.interval === 'custom'
			? createCustomTimeMachine(timeMachine.previous, timeMachine.current[0])
			: createTimeMachine(timeMachine.interval, timeMachine.previous)
		: undefined;

	const descriptionKey = `analytics.metric.description.${data?.analytics.metricGroup.metric.id}`;

	useEffect(() => {
		if (groupSelection.loading) {
			return;
		}

		// noinspection JSIgnoredPromiseFromCall
		fetchAnalyticsMetric();
	}, [groupSelection.loading, groupSelection.groupsInSelection, fetchAnalyticsMetric]);

	const isLoading = (!data && !error) || groupSelection.loading;

	const breadCrumbComponents = data
		? [
				{
					id: 'analytics',
					name: t('analytics.breadcrumb.analytics'),
					to: `/analytics${new URL(window.location.href).search}`,
				},
				{
					id: 'group',
					name: t(`analytics.metrics.groupTitle.${params.metricGroupID}`),
					readOnly: true,
				},
				{
					id: 'metric',
					name: upperFirst(t(`analytics.metrics.${data.analytics.metricGroup.metric.id}`)),
					readOnly: true,
				},
			]
		: [];

	// Use 364 to account for leap years
	const intervalIsShorterThanOneYear =
		moment(filters.endDate).diff(moment(filters.startDate), 'days') < 364;

	const isEngagementENPS = data?.analytics.metricGroup.metric.id === 'engagement.enps';

	const isNPSTab = params.tabID === 'nps';

	return (
		<>
			<Container mt={4}>
				{isLoading ? <Breadcrumbs.Skeleton /> : <Breadcrumbs components={breadCrumbComponents} />}
			</Container>
			<Jumbo
				title={
					data?.analytics.metricGroup.metric.id
						? upperFirst(t(`analytics.metrics.${data.analytics.metricGroup.metric.id}`))
						: undefined
				}
			>
				<FlexGap alignItems="center" flexWrap="wrap" gap={tokens.gap.list} mt={3}>
					<Box>
						<Label mb={2}>{t('analytics.cycles.group')}</Label>
						<GroupSelectorNew
							canSelect={props.selectableGroupFilter}
							onChange={(newGroupSelection) => {
								groupSelection.updateExplicitGroupSelection(newGroupSelection);
							}}
							allGroups={groupSelection.allGroups}
							groupSelection={groupSelection.groupSelection}
							hasExplicitSelection={groupSelection.hasExplicitGroupSelection}
							groupsInSelection={groupSelection.groupsInSelection}
						/>
					</Box>
					<Box flexGrow={1} flexShrink={1} />
					<Box flexShrink={0}>
						<Label mb={2}>{t('analytics.treamsScore.timePeriod')}</Label>
						<Flex alignItems="center" style={{ gap: tokens.gap.list }}>
							<DateIntervalNavigation
								value={timeMachine}
								onChange={props.onChangeTimeMachine}
								workspaceStartDate={
									configuration.data?.startDate
										? moment(configuration.data.startDate).toDate()
										: undefined
								}
							/>
							<AnalyticsUserFilterSelector onChange={(filter) => setUserFilter(filter)} />
						</Flex>
					</Box>
				</FlexGap>
			</Jumbo>
			<Container py={4}>
				{!isEngagement && (
					<>
						<Box mb={4}>
							<Heading3 mb={2}>{t('analytics.metric.description.title')}</Heading3>
							{isLoading ? (
								<Paragraph.Skeleton lines={2} />
							) : (
								<Paragraph>
									{t(descriptionKey) !== descriptionKey
										? t(descriptionKey)
										: t('analytics.metric.description.empty')}
								</Paragraph>
							)}
						</Box>

						{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

						{!error && (
							<Grid>
								<Card>
									{isLoading ? (
										<MetricValue.Skeleton />
									) : (
										<>
											{previousTimeMachine && (
												<MetricValue
													variables={sharedVariables}
													timeMachine={previousTimeMachine}
													groups={groupSelection.groupsInSelection}
													userFilter={userFilter}
													engagementOfficerMetricsOnly={props.engagementOfficerMetricsOnly}
													previous
												/>
											)}
										</>
									)}
								</Card>

								<Card>
									{isLoading ? (
										<MetricValue.Skeleton />
									) : (
										<MetricValue
											variables={sharedVariables}
											timeMachine={timeMachine}
											groups={groupSelection.groupsInSelection}
											userFilter={userFilter}
											engagementOfficerMetricsOnly={props.engagementOfficerMetricsOnly}
										/>
									)}
								</Card>

								<Box px={3}>
									<Heading3 mb={2} mt={3}>
										{t(`analytics.metric.kpis`)}
									</Heading3>

									<Threshold
										metric={data?.analytics.metricGroup.metric as graphql.AnalyticsMetric}
										signal="good"
									/>
									<Threshold
										metric={data?.analytics.metricGroup.metric as graphql.AnalyticsMetric}
										signal="bad"
									/>
								</Box>
							</Grid>
						)}
					</>
				)}

				{isEngagement &&
					intervalIsShorterThanOneYear &&
					!hasSelectedEngagementRequest &&
					!isNPSTab && (
						<Callout color="blue">
							<Box>
								<Subheading>{t('engagement.metric.callout.dateInterval.title')}</Subheading>
								<Paragraph>
									{t('engagement.metric.callout.dateInterval.disclaimer', {
										startDate: moment(filters.startDate).format('L'),
										endDate: moment(filters.endDate).format('L'),
										actualStartDate: moment(filters.endDate).subtract(1, 'year').format('L'),
										actualEndDate: moment(filters.endDate).format('L'),
									})}
								</Paragraph>
							</Box>
						</Callout>
					)}

				{data?.analytics.metricGroup.metric &&
					hasDetails(data.analytics.metricGroup.metric as graphql.AnalyticsMetric) &&
					(data.analytics.metricGroup.metric as any).details.length !== 0 && (
						<AnalyticsMetricDetails
							ids={(data.analytics.metricGroup.metric as any).details}
							type={sharedVariables.type}
							filters={filters}
							timeMachine={timeMachine}
						/>
					)}

				{data?.analytics.metricGroup.metric.id.startsWith('engagement.') && (
					<Box mt={4}>
						<EngagementAnalytics
							eNPS={isEngagementENPS}
							filters={{
								startDate: timeMachine.current[0],
								endDate: timeMachine.current[1],
								groupIDs: groupSelection.groupsInSelection.map(({ id }) => id),
								userFilter,
							}}
							interval={timeMachine.interval}
							onRequestIDChange={(id) => setHasSelectedEngagementRequest(!!id)}
						/>
					</Box>
				)}
			</Container>
		</>
	);
}
